import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import useModalFeedback from '../hooks/useModalFeedback'
import PhotoAttachmentPicker from '../components/PhotoAttachmentPicker'
import { getBaseUrl } from '../utils/apiConfig'

const API_BASE_URL = getBaseUrl()

const EXPENSE_CATEGORIES = ['Material', 'Rental', 'Petrol', 'Subcon', 'Others']

export default function ExpenseForm() {
  const { showMessage, modalElement } = useModalFeedback()
  const location = useLocation()
  const [employees, setEmployees] = useState([])
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
  const [expenseCategory, setExpenseCategory] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formOpenedAt] = useState(() => Date.now())

  // Determine expense type from current route
  const expenseType = location.pathname.includes('advanced') ? 'Advanced' : 'Receipt'
  const expenseUploadCategory = expenseType.toLowerCase() === 'advanced' ? 'advanced' : 'receipt'
  const pageTitle = expenseType === 'Advanced' ? 'Advanced Expense Form' : 'Receipt Expense Form'

  useEffect(() => {
    fetchEmployees()
  }, [])

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/employees/public-list`)
      const payload = await response.json()
      const allEmployees = payload?.data || []
      setEmployees(allEmployees)
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch employees:', error)
      setLoading(false)
    }
  }

  const addFiles = (newFiles) => {
    setSelectedFiles((prev) => [...prev, ...newFiles])
  }

  const removeFile = (fileId) => {
    setSelectedFiles(selectedFiles.filter(f => f.id !== fileId))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedEmployeeId) {
      showMessage('Please select an employee', 'Validation')
      return
    }

    // Category is only required for Receipt expenses, not for Advanced expenses
    if (expenseType !== 'Advanced' && !expenseCategory) {
      showMessage('Please select a category', 'Validation')
      return
    }

    if (selectedFiles.length === 0) {
      showMessage('Please select at least one file', 'Validation')
      return
    }

    try {
      setUploadingFiles(true)

      // Create FormData with all files
      const uploadFormData = new FormData()
      selectedFiles.forEach((fileObj) => {
        uploadFormData.append('files', fileObj.file)
      })
      uploadFormData.append('employee_id', String(selectedEmployeeId))
      uploadFormData.append('expense_type', expenseType)
      uploadFormData.append('expense_category', expenseCategory)
      uploadFormData.append('submitted_at', String(formOpenedAt))
      uploadFormData.append('website', '')

      // Determine API base URL
      const apiBase = import.meta.env.VITE_API_URL ||
        (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
          ? `${window.location.origin}/api`
          : 'http://localhost:5000/api')

      // Call public upload endpoint
      const response = await fetch(`${apiBase}/expenses/public-upload/${expenseUploadCategory}`, {
        method: 'POST',
        body: uploadFormData
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('Upload error response:', { status: response.status, data })
        throw new Error(data.message || data.error || `Upload failed with status ${response.status}`)
      }

      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        setSelectedEmployeeId('')
        setExpenseCategory('')
        setSelectedFiles([])
      }, 3000)
    } catch (error) {
      console.error('Failed to upload files:', error)
      showMessage(`Upload failed: ${error.message || 'Unknown error'}`, 'Error')
    } finally {
      setUploadingFiles(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 via-blue-50 to-white px-3 py-4 sm:px-4 sm:py-6 lg:py-8">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-center rounded-2xl border border-slate-200 bg-white p-4 sm:p-8 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
            <p className="text-sm text-slate-600">Loading employee list...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-blue-50 to-white px-3 py-4 sm:px-4 sm:py-6 lg:py-8">
      <div className="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-5 sm:px-6 sm:py-6">
          <p className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
            Public Form
          </p>
          <h1 className="mt-3 text-2xl font-bold text-slate-900 sm:text-3xl">{pageTitle}</h1>
          <p className="mt-2 text-sm text-slate-600">Submit your {expenseType.toLowerCase()} expense for approval.</p>
        </div>

        {submitted ? (
          <div className="px-4 py-6 sm:px-6">
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 sm:p-6 text-center">
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-lg font-bold text-green-700">
                OK
              </div>
              <h2 className="text-xl font-semibold text-green-700">Success!</h2>
              <p className="mt-2 text-green-700">Your {selectedFiles.length} expense record(s) have been submitted successfully.</p>
              <p className="mt-3 text-sm text-slate-600">You can now submit another expense or close this window.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 px-4 py-5 sm:px-6 sm:py-6">
            {/* Employee Name Dropdown */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800">
                Employee Name
              </label>
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                disabled={uploadingFiles}
                className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
              >
                <option value="">-- Select Employee --</option>
                {employees.map((emp) => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Dropdown - only show for Receipt expenses */}
            {expenseType !== 'Advanced' && (
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800">
                  Category
                </label>
                <select
                  value={expenseCategory}
                  onChange={(e) => setExpenseCategory(e.target.value)}
                  disabled={uploadingFiles}
                  className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                >
                  <option value="">-- Select Category --</option>
                  {EXPENSE_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            )}

            <PhotoAttachmentPicker
              label="Upload Attachments"
              files={selectedFiles}
              onAddFiles={addFiles}
              onRemoveFile={removeFile}
              disabled={uploadingFiles}
              multiple
              allowFileUploads
            />

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">How it works:</span> Each upload creates 1 expense record with submission date. You can upload photo or file formats such as PDF, DOCX, XLSX, PNG, JPG, and ZIP. You fill in amount and project later. Admin will fill in the receipt date.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setSelectedEmployeeId('')
                  setExpenseCategory('')
                  setSelectedFiles([])
                }}
                disabled={uploadingFiles}
                className="h-11 rounded-lg border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[140px]"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={uploadingFiles || selectedFiles.length === 0}
                className="h-11 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400 sm:min-w-[170px]"
              >
                {uploadingFiles ? 'Submitting...' : `Submit (${selectedFiles.length})`}
              </button>
            </div>
          </form>
        )}

        {modalElement}
      </div>
    </div>
  )
}
